import { useEffect } from 'react';
import { PageSeo, SITE_NAME, SOCIAL_IMAGE, absoluteUrl } from '../lib/seo';

interface SeoProps extends PageSeo {
  /** Overrides the default social card image. Must be an absolute URL. */
  image?: string;
  /** Keeps a page out of the index. Used for URLs that are about one venue. */
  noindex?: boolean;
}

/**
 * Marks the tags this component owns, so it can rewrite its own on a route
 * change without touching the analytics, icon or manifest tags beside them.
 */
const MANAGED = 'data-seo-managed';

/**
 * Find-or-create one head element, matched on the attribute that identifies
 * it — `name` for the standard metas, `property` for Open Graph's, `rel` for
 * the canonical link.
 */
function upsert(
  tag: 'meta' | 'link',
  key: 'name' | 'property' | 'rel',
  keyValue: string,
  valueAttr: 'content' | 'href',
  value: string
): void {
  const selector = `${tag}[${key}="${keyValue}"]`;
  let element = document.head.querySelector<HTMLElement>(selector);

  if (!element) {
    element = document.createElement(tag);
    element.setAttribute(key, keyValue);
    element.setAttribute(MANAGED, '');
    document.head.appendChild(element);
  }

  element.setAttribute(valueAttr, value);
}

/**
 * The head tags that describe the current page.
 *
 * Every route renders exactly one of these, and each render writes the whole
 * set — title, description, canonical, robots, Open Graph and Twitter. Writing
 * all of them every time is what makes the head a function of the route rather
 * than of the order routes were visited: a page that sets `noindex` cannot
 * leave that behind on the next page, because the next page states
 * `index, follow` rather than staying silent.
 *
 * Tags are updated in place instead of removed on unmount, so a route change
 * never leaves the document momentarily undescribed — the crawler that renders
 * this page sees one consistent head, not a gap.
 *
 * A caveat worth knowing: this runs in the browser, so Googlebot (which
 * renders JavaScript) sees these values, but the social crawlers — Facebook,
 * X, LinkedIn, WhatsApp — do not run scripts and read the static defaults in
 * `public/index.html` instead. Those defaults are written to stand in for any
 * page on the site for exactly that reason.
 */
export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path,
  image = SOCIAL_IMAGE,
  noindex = false,
}) => {
  useEffect(() => {
    const url = absoluteUrl(path);

    document.title = title;

    upsert('meta', 'name', 'description', 'content', description);
    upsert(
      'meta',
      'name',
      'robots',
      'content',
      noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'
    );
    upsert('link', 'rel', 'canonical', 'href', url);

    upsert('meta', 'property', 'og:type', 'content', 'website');
    upsert('meta', 'property', 'og:site_name', 'content', SITE_NAME);
    upsert('meta', 'property', 'og:title', 'content', title);
    upsert('meta', 'property', 'og:description', 'content', description);
    upsert('meta', 'property', 'og:url', 'content', url);
    upsert('meta', 'property', 'og:image', 'content', image);

    upsert('meta', 'name', 'twitter:card', 'content', 'summary_large_image');
    upsert('meta', 'name', 'twitter:title', 'content', title);
    upsert('meta', 'name', 'twitter:description', 'content', description);
    upsert('meta', 'name', 'twitter:image', 'content', image);
  }, [title, description, path, image, noindex]);

  return null;
};
